<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import useBanks from "@/internal/composables/useBanks";
import useBankAccount from "@/internal/modules/providers/composables/useBankAccount";
import { Hotel } from "@/internal/modules/providers/interfaces/Hotel";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { reactive, ref, watch } from "vue";
import { useToast } from "vue-toastification";
import useHotelBankAccount from "../composables/useHotelBankAccount";
import useHotelBankPaymentTypes from "../composables/useHotelBankPaymentTypes";
import useHotelBillingInformation from "../composables/useHotelBillingInformation";

interface Props {
  hotel: Hotel;
}

const props = defineProps<Props>();
const toast = useToast();

const { hasPermissionUser } = useHasPermissionUser();

const { hotelBankPaymentTypes } = useHotelBankPaymentTypes({
  errorHandling: false,
});

const { data: billingInformation } = useHotelBillingInformation({
  params: {
    id: props.hotel.id,
  },
  immediate: true,
});

const { data: banks } = useBanks({
  immediate: true,
  erroHandling: false,
});

const { data, isLoading, createAsync, refetch, page } = useHotelBankAccount({
  params: {
    id: props.hotel.id,
  },
  config: {
    errorHandling: false,
  },
});

const { deleteBankAccount, restoreBankAccount, updateBankAccount } =
  useBankAccount({
    immediate: false,
  });

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = pageNumber;
    refetch();
  }
};

type Form = {
  id: number | null;
  accountId: string;
  description: string;
  bankId: string;
  accountNumber: string;
  rfc: string;
};

const form = reactive<Form>({
  id: null,
  accountId: "",
  description: "",
  bankId: "",
  accountNumber: "",
  rfc: "",
});

watch(billingInformation, (value) => {
  form.rfc = value?.rfc || "";
});

const actionModal = ref(false);

const handleActionModal = () => {
  actionModal.value = !actionModal.value;
};

const handleCreateHotelBankAccount = async () => {
  try {
    await createAsync({
      account_id: form.accountId,
      paymentType: form.description,
      bankId: Number(form.bankId),
      accountNumber: form.accountNumber,
      rfc: form.rfc!,
      hotelId: props.hotel.id,
    });

    toast.success("Cuenta bancaria creada correctamente");
    refetch();
    handleActionModal();
    clearForm();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleUpdateHotelBanKAccount = async () => {
  try {
    await updateBankAccount.mutateAsync({
      id: form.id!,
      account_id: form.accountId,
      paymentType: form.description,
      bankId: Number(form.bankId),
      accountNumber: form.accountNumber,
      rfc: form.rfc!,
    });

    toast.success("Cuenta bancaria actualizada correctamente");
    refetch();
    handleActionModal();
    clearForm();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleDeleteHotelBankAccount = async (id: number) => {
  try {
    await deleteBankAccount.mutateAsync(id);
    toast.success("Cuenta bancaria desactivada correctamente");
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleRestoreHotelBankAccount = async (id: number) => {
  try {
    await restoreBankAccount.mutateAsync(id);
    toast.success("Cuenta bancaria restaurada correctamente");
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const clearForm = () => {
  form.id = null;
  form.accountId = "";
  form.description = "";
  form.bankId = "";
  form.accountNumber = "";
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between items-center">
      <h2 class="text-lg font-semibold text-gray-800">Cuentas bancarias</h2>

      <StyledButton
        v-if="
          hasPermissionUser(
            'create-bankAccounts-internal',
            'App\\Models\\Hotel',
          ) &&
          hasPermissionUser('viewAny-internal', 'App\\Models\\Bank') &&
          hasPermissionUser(
            'view-billingInformation-internal',
            'App\\Models\\Hotel',
          )
        "
        class="text-white"
        @click="handleActionModal"
        :disabled="!billingInformation?.rfc"
        :class="
          !billingInformation?.rfc
            ? 'cursor-not-allowed bg-blue-300 '
            : 'bg-blue-500 hover:bg-blue-800'
        "
        v-tooltip="
          !billingInformation?.rfc
            ? 'Debes tener un RFC registrado en la sección de datos fiscales'
            : ''
        "
      >
        <span class="material-symbols-outlined">add</span>
        Agregar
      </StyledButton>
    </div>

    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                  >
                    Id de pago
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Descripción de pago
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Banco
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Numero de cuenta
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    RFC
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  ></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr
                  v-for="bankAccount in data?.data || []"
                  :key="bankAccount.id"
                >
                  <td
                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 flex items-center gap-2"
                  >
                    <span
                      v-tooltip="bankAccount.active ? 'Activo' : 'Inactivo'"
                      class="material-symbols-outlined cursor-pointer"
                      :class="
                        bankAccount.active ? 'text-green-500' : 'text-red-500'
                      "
                    >
                      {{ bankAccount.active ? "check_circle" : "cancel" }}
                    </span>
                    <p>{{ bankAccount.accountId }}</p>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ bankAccount.paymentType }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ bankAccount.bankName }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ bankAccount.accountNumber }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ bankAccount.rfc }}
                  </td>
                  <td
                    class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                  >
                    <div class="flex justify-start gap-2">
                      <HasPermission
                        :name-permission="'update-bankAccounts-internal'"
                        :entityType="'App\\Models\\Hotel'"
                      >
                        <button
                          v-if="bankAccount.active"
                          class="text-blue-500 hover:text-blue-800"
                          @click="
                            form.id = bankAccount.id;
                            form.accountId = bankAccount.accountId;
                            form.description = bankAccount.paymentType;
                            form.bankId = bankAccount.bankId.toString();
                            form.accountNumber = bankAccount.accountNumber;
                            form.rfc = bankAccount.rfc;
                            handleActionModal();
                          "
                        >
                          <span class="material-symbols-outlined">edit</span>
                        </button>
                      </HasPermission>

                      <HasPermission
                        :name-permission="'delete-bankAccounts-internal'"
                        :entityType="'App\\Models\\Hotel'"
                      >
                        <button
                          v-if="bankAccount.active"
                          class="text-red-500 hover:text-red-800"
                          @click="handleDeleteHotelBankAccount(bankAccount.id)"
                        >
                          <span class="material-symbols-outlined">delete</span>
                        </button>
                      </HasPermission>

                      <HasPermission
                        :name-permission="'restore-bankAccounts-internal'"
                        :entityType="'App\\Models\\Hotel'"
                      >
                        <button
                          v-if="!bankAccount.active"
                          class="text-green-500 hover:text-green-800"
                          @click="handleRestoreHotelBankAccount(bankAccount.id)"
                        >
                          <span class="material-symbols-outlined">restore</span>
                        </button>
                      </HasPermission>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end">
      <PaginateNav
        :page="page"
        :pagination="data"
        :data="data?.data || []"
        :handle-page="handlePage"
      />
    </div>
  </ShadowBox>

  <ModalComponent
    :model-value="actionModal"
    @update:model-value="
      () => {
        handleActionModal();
        clearForm();
      }
    "
  >
    <template #modalBody>
      <div class="p-4">
        <form
          @submit.prevent="
            form.id
              ? handleUpdateHotelBanKAccount()
              : handleCreateHotelBankAccount()
          "
          class="flex flex-col gap-4"
        >
          <StyledSelect
            :options="
              hotelBankPaymentTypes?.paymentTypes.map((payment: string) => {
                return {
                  value: payment,
                  label: payment,
                };
              }) || []
            "
            v-model="form.description"
            label="Descripción"
          />

          <StyledInput
            v-model="form.accountId"
            label="Id de pago"
            placeholder="Id de pago"
            :is-required="true"
            :max-length="4"
          />

          <StyledSelect
            v-model="form.bankId"
            label="Banco"
            :options="
              banks?.map((bank) => {
                return {
                  value: bank.id,
                  label: bank.name,
                };
              }) || []
            "
            optionValue="name"
            optionLabel="name"
            :is-required="true"
          />

          <StyledInput
            v-model="form.accountNumber"
            label="Número de cuenta"
            placeholder="Número de cuenta"
            :is-required="true"
            :max-length="18"
          />

          <p class="text-gray-500 text-lg mt-2">
            RFC: {{ billingInformation?.rfc }}
          </p>

          <StyledButton
            class="ml-auto text-white bg-blue-500 hover:bg-blue-800"
          >
            Guardar
          </StyledButton>
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
